import { keyframes } from '@emotion/react';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getRunners,
  getRunnersWithMissingTiming,
  getUnregisteredRunners,
  hasTimingGaps,
} from '@/core/services/event-admin';
import { db } from '@/core/services/firebase';
import type { AdminRunner } from '@/core/types';
import { RunnerDetailModal } from '../components/RunnerDetailModal';
import { RunnerLinkModal } from '../components/RunnerLinkModal';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

type FilterType = 'all' | 'nodata' | 'missing' | 'unregistered';

export function ResultsPage() {
  const { raceAlias } = useParams<{ raceAlias: string }>();
  const theme = useTheme();
  const adminGradient = theme.custom?.adminGradient || theme.custom?.gradient || theme.palette.primary.main;

  const [allRunners, setAllRunners] = useState<AdminRunner[]>([]);
  const [noDataRunners, setNoDataRunners] = useState<AdminRunner[]>([]);
  const [missingTimingRunners, setMissingTimingRunners] = useState<AdminRunner[]>([]);
  const [unregisteredRunners, setUnregisteredRunners] = useState<AdminRunner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [realtime, setRealtime] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  // モーダル用state
  const [detailRunner, setDetailRunner] = useState<AdminRunner | null>(null);
  const [linkRunner, setLinkRunner] = useState<AdminRunner | null>(null);

  // ランナーデータから欠損・未登録を計算
  const calculateStats = useCallback((runners: AdminRunner[]) => {
    // データなし = timingが空
    const noData = runners.filter((r) => !r.timing || r.timing.length === 0);
    // データ欠損 = locationIdが飛んでいる
    const missing = runners.filter((r) => hasTimingGaps(r.timing));
    const unregistered = runners.filter((r) => r.timing && r.timing.length > 0 && !r.fullName);
    setAllRunners(runners);
    setNoDataRunners(noData);
    setMissingTimingRunners(missing);
    setUnregisteredRunners(unregistered);
  }, []);

  // リアルタイム更新
  useEffect(() => {
    if (!raceAlias || !realtime) return;

    setLoading(true);
    setError(null);

    const runnersRef = collection(db, 'runners');
    const q = query(runnersRef, where('raceAlias', '==', raceAlias), orderBy('updatedAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            }) as AdminRunner
        );
        calculateStats(data);
        setLastFetchedAt(new Date());
        setLoading(false);
      },
      (err) => {
        console.error('Realtime update failed:', err);
        setError('リアルタイム更新に失敗しました');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [raceAlias, realtime, calculateStats]);

  // 手動更新
  const fetchData = useCallback(async () => {
    if (!raceAlias) return;

    setLoading(true);
    setError(null);

    try {
      const [all, missing, unregistered] = await Promise.all([
        getRunners(raceAlias),
        getRunnersWithMissingTiming(raceAlias),
        getUnregisteredRunners(raceAlias),
      ]);
      const noData = all.filter((r) => !r.timing || r.timing.length === 0);
      setAllRunners(all);
      setNoDataRunners(noData);
      setMissingTimingRunners(missing);
      setUnregisteredRunners(unregistered);
      setLastFetchedAt(new Date());
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [raceAlias]);

  // 初回読み込み（リアルタイムでない場合）
  useEffect(() => {
    if (!realtime) {
      fetchData();
    }
  }, [fetchData, realtime]);

  const handleFilterChange = (newFilter: FilterType) => {
    setFilter(newFilter);
  };

  // フィルター適用後のデータ
  const filteredData = useMemo(() => {
    switch (filter) {
      case 'nodata':
        return noDataRunners;
      case 'missing':
        return missingTimingRunners;
      case 'unregistered':
        return unregisteredRunners;
      default:
        return allRunners;
    }
  }, [filter, allRunners, noDataRunners, missingTimingRunners, unregisteredRunners]);

  const handleDownloadCsv = () => {
    // RFC4180準拠のCSVエスケープ
    const escapeCsv = (value: string | number): string => {
      const str = String(value ?? '');
      if (/[",\n\r]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headers = ['No.', 'ゼッケン', '計測データ', '計測地点数', 'ID'];
    const csvRows = filteredData.map((r, index) => [
      escapeCsv(index + 1),
      escapeCsv(r.bib),
      escapeCsv(r.timing?.map((entry) => `${entry.locationId}/${entry.time}`).join(', ') || ''),
      escapeCsv(r.timing?.length || 0),
      escapeCsv(r.id),
    ]);

    const csvContent = [headers.join(','), ...csvRows.map((row) => row.join(','))].join('\n');

    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, csvContent], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `runners_${filter}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // 未登録ランナー判定
  const isUnregistered = (runner: AdminRunner) => runner.timing && runner.timing.length > 0 && !runner.fullName;

  const columns: GridColDef<AdminRunner>[] = [
    {
      field: 'actions',
      headerName: '操作',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', height: '100%' }}>
          <Tooltip title="詳細">
            <IconButton size="small" onClick={() => setDetailRunner(params.row)}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {isUnregistered(params.row) && (
            <Tooltip title="紐付け">
              <IconButton
                size="small"
                onClick={() => setLinkRunner(params.row)}
                sx={{
                  '& .MuiSvgIcon-root': {
                    background: adminGradient,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  },
                }}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
    { field: 'bib', headerName: 'ゼッケン', width: 100 },
    {
      field: 'fullName',
      headerName: '氏名',
      width: 150,
      renderCell: (params) => {
        const runner = params.row;
        if (runner.fullName) {
          return runner.fullName;
        }
        if (runner.tempFullName) {
          return (
            <Tooltip title="計測データからの仮名">
              <Chip
                label={runner.tempFullName}
                size="small"
                sx={{
                  border: '2px solid transparent',
                  backgroundImage: `linear-gradient(#fff, #fff), ${adminGradient}`,
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                }}
              />
            </Tooltip>
          );
        }
        return '-';
      },
    },
    {
      field: 'timing',
      headerName: '計測データ',
      width: 350,
      valueGetter: (_value, row) => row.timing?.map((entry) => `${entry.locationId}/${entry.time}`).join(', ') || '',
    },
    {
      field: 'timingCount',
      headerName: '計測地点数',
      width: 240,
      valueGetter: (_value, row) => row.timing?.length || 0,
      renderCell: (params) => {
        const count = params.value as number;
        const runner = params.row;

        if (count === 0) {
          // データなしは異常ではない（スタート前は全員そう）
          return <Chip label="データなし" size="small" />;
        }
        if (hasTimingGaps(runner.timing)) {
          // locationIdが飛んでいる = データ欠損
          return <Chip label={`${count}件（欠損あり）`} color="warning" size="small" />;
        }
        return <Chip label={`${count}件`} color="success" size="small" />;
      },
    },
    { field: 'id', headerName: 'ID', width: 200 },
  ];

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3, animation: `${fadeInUp} 0.6s ease-out` }}>
          {error}
        </Alert>
      )}

      {/* 統計カード */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        {/* 統計 + コントロール */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          {/* 統計ボックス */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, flexGrow: 1 }}>
            <Box
              sx={{
                width: 120,
                px: 2,
                py: 1,
                borderRadius: '8px',
                backgroundColor: 'success.main',
                color: 'success.contrastText',
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                全体
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {allRunners.length}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 120,
                px: 2,
                py: 1,
                borderRadius: '8px',
                backgroundColor: 'warning.main',
                color: 'warning.contrastText',
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                データ欠損
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {missingTimingRunners.length}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 120,
                px: 2,
                py: 1,
                borderRadius: '8px',
                backgroundColor: 'error.main',
                color: 'error.contrastText',
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                未登録ランナー
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {unregisteredRunners.length}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 120,
                px: 2,
                py: 1,
                borderRadius: '8px',
                backgroundColor: 'grey.500',
                color: 'grey.contrastText',
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                データなし
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {noDataRunners.length}
              </Typography>
            </Box>
          </Box>

          {/* コントロール */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            <FormControlLabel
              control={<Switch checked={realtime} onChange={(e) => setRealtime(e.target.checked)} color="success" />}
              label="リアルタイム更新"
              sx={{ mr: 0 }}
            />
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCsv}
              disabled={filteredData.length === 0}
              sx={{
                borderRadius: '16px',
                border: '2px solid transparent',
                backgroundImage: `linear-gradient(#fff, #fff), ${adminGradient}`,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                color: 'text.primary',
                '&:hover': {
                  backgroundImage: `linear-gradient(#f5f5f5, #f5f5f5), ${adminGradient}`,
                },
                '&.Mui-disabled': {
                  backgroundImage: 'none',
                  border: '1px solid',
                  borderColor: 'action.disabled',
                },
              }}
            >
              CSV
            </Button>
            {!realtime && (
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchData}
                sx={{
                  borderRadius: '16px',
                  border: '2px solid transparent',
                  backgroundImage: `linear-gradient(#fff, #fff), ${adminGradient}`,
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundImage: `linear-gradient(#f5f5f5, #f5f5f5), ${adminGradient}`,
                  },
                }}
              >
                更新
              </Button>
            )}
          </Box>
        </Box>

        {/* フィルター */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label="すべて"
            onClick={() => handleFilterChange('all')}
            clickable
            sx={{
              fontWeight: filter === 'all' ? 600 : 400,
              ...(filter === 'all' && {
                background: adminGradient,
                '& .MuiChip-label': { color: '#fff' },
              }),
            }}
          />
          <Chip
            label={`データ欠損 (${missingTimingRunners.length})`}
            onClick={() => handleFilterChange('missing')}
            clickable
            color={filter === 'missing' ? 'warning' : 'default'}
            sx={{ fontWeight: filter === 'missing' ? 600 : 400 }}
          />
          <Chip
            label={`未登録ランナー (${unregisteredRunners.length})`}
            onClick={() => handleFilterChange('unregistered')}
            clickable
            color={filter === 'unregistered' ? 'error' : 'default'}
            sx={{ fontWeight: filter === 'unregistered' ? 600 : 400 }}
          />
          <Chip
            label={`データなし (${noDataRunners.length})`}
            onClick={() => handleFilterChange('nodata')}
            clickable
            sx={{
              fontWeight: filter === 'nodata' ? 600 : 400,
              ...(filter === 'nodata' && {
                backgroundColor: 'grey.500',
                color: '#fff',
              }),
            }}
          />
        </Box>
      </Paper>

      {/* 最終更新日時 */}
      {lastFetchedAt && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'right' }}>
          最終更新: {lastFetchedAt.toLocaleTimeString('ja-JP')}
        </Typography>
      )}

      {/* データグリッド */}
      <Paper
        sx={{
          height: 600,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          animation: `${fadeInUp} 0.6s ease-out 0.1s both`,
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'action.hover',
            borderBottom: '2px solid',
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <DataGrid
          rows={filteredData}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
        />
      </Paper>

      {/* 詳細モーダル */}
      <RunnerDetailModal runner={detailRunner} onClose={() => setDetailRunner(null)} />

      {/* 紐付けモーダル */}
      {raceAlias && (
        <RunnerLinkModal
          runner={linkRunner}
          raceAlias={raceAlias}
          onClose={() => setLinkRunner(null)}
          onLinked={() => {
            setLinkRunner(null);
            fetchData();
          }}
        />
      )}
    </Box>
  );
}
